import type React from 'react';
import { Accordion, Anchor, Box, Heading, Icon, Paragraph, Text, ZillowLogo } from '@zillow/constellation-10';
import { IconDollarSignCircleFilled, IconTrendingFilled } from '@zillow/constellation-icons';
import EsusuIcon from '../../assets/EsusuIcon.svg';
import EsusuLogo from '../../assets/EsusuLogo.png';
import Image from 'next/image';
import { BenefitSection } from './elements/BenefitSection';
import { ClickstreamAuthTriggerSource, ClickstreamTriggerObject, emitFaqClickEvent } from '../../analytics/events';
import RequestZillowPaymentsModal from './elements/RequestZillowPaymentsModal';

export default function OverviewScreen() {
  return (
    <Box css={{ maxWidth: '768px' }}>
      <Box
        css={{
          display: 'flex',
          gap: '{spacing.300}',
          alignItems: 'center',
          marginBottom: '{spacing.loose}',
          marginTop: '{spacing.loose}',
        }}
      >
        <Box css={{ height: '{spacing.600}' }}>
          <ZillowLogo role="img" />
        </Box>
        <Text css={{ fontSize: '{fontSizes.450}', color: 'black', fontWeight: '{fontWeights.bold}' }}>|</Text>
        <Box css={{ height: '{spacing.600}' }}>
          <Image src={EsusuLogo} alt="Esusu Logo" width={100} height={24} />
        </Box>
      </Box>

      <Heading level={3} css={{ marginBottom: '{spacing.800}' }}>
        Start building your credit
      </Heading>

      <Paragraph css={{ fontSize: '{fontSizes.450}', marginBottom: '{spacing.800}' }}>
        Here&apos;s how a credit building membership can help:
      </Paragraph>

      <Box css={{ marginBottom: '{spacing.800}' }}>
        <BenefitSection
          icon={<Image src={EsusuIcon} alt="Esusu Icon" width={34} height={34} />}
          title="Sign up for $20 per year"
          backgroundColor="green"
        >
          Join over <strong>5 million renters</strong> who report their payments with our trusted partner, Esusu.
        </BenefitSection>

        <BenefitSection
          icon={
            <Icon size="xl" css={{ color: 'icon.onHero.express.insight.hero' }}>
              <IconDollarSignCircleFilled />
            </Icon>
          }
          title="Esusu reports your on-time payments"
          backgroundColor="green"
        >
          There&apos;s no hard pulls on your credit or hidden fees, plus late or missed rent is <strong>never</strong>{' '}
          reported. However, consistent on-time payments help build your credit.
        </BenefitSection>

        <BenefitSection
          icon={
            <Icon size="xl" css={{ color: 'icon.onHero.express.insight.hero' }}>
              <IconTrendingFilled />
            </Icon>
          }
          title="Prepare for the future"
          backgroundColor="green"
        >
          According to Esusu’s 2024 impact report, members have seen an average <strong>+45 points</strong> to their
          credit.* Rent reporting can help you start building a credit profile that supports your future home buying
          goals, without taking on debt.
        </BenefitSection>
      </Box>

      <Box css={{ marginBottom: '{spacing.1200}' }}>
        <Accordion.Root>
          <Accordion.Item value="why-partner" onClick={() => emitFaqClickEvent('why_esusu')}>
            <Accordion.Header>
              <Paragraph>Why is Zillow partnering with Esusu?</Paragraph>
            </Accordion.Header>
            <Accordion.Panel>
              <Paragraph css={{ marginBottom: '{spacing.default}' }}>
                Zillow is built on a deep understanding of movers&apos; needs and a motivation to help you accomplish
                your moving goals, whether that&apos;s choosing your next rental or preparing to buy your first home. We
                believe rent reporting can help you start building a credit profile that supports your future home
                buying goals, without taking on debt.
              </Paragraph>
              <Paragraph css={{ marginBottom: '{spacing.default}' }}>
                We&apos;re partnering with{' '}
                <Anchor href="https://esusurent.com/" target="_blank">
                  Esusu
                </Anchor>
                , a financial technology company dedicated to expanding fair credit and financial access. Esusu helps
                renters build credit while giving property partners tools that promote financial stability.
              </Paragraph>
              <Paragraph>
                Through our partnership, you can report your rent for an <strong>exclusive annual rate of $20</strong>,{' '}
                helping you get credit for one of your current largest monthly expenses, your rent.
              </Paragraph>
            </Accordion.Panel>
          </Accordion.Item>
          <Accordion.Item value="free-option">
            <Accordion.Header onClick={() => emitFaqClickEvent('zrm_version')}>
              <Paragraph>Is there a free option?</Paragraph>
            </Accordion.Header>
            <Accordion.Panel>
              <Paragraph css={{ marginBottom: '{spacing.default}' }}>
                Yes, when your landlord collects rent with Zillow payments you&apos;ll have the choice to opt-in to free
                rent reporting. Plus, there&apos;s no fee to pay rent with Zillow when you pay with a direct bank
                transfer.
              </Paragraph>
              <Paragraph>
                Interested in this option?{' '}
                <RequestZillowPaymentsModal
                  ctaText="Ask your landlord to use Zillow payments today."
                  clickstreamTriggerObject={ClickstreamTriggerObject.FAQ_UPSELL}
                  authTriggerSource={ClickstreamAuthTriggerSource.ZRM_REQUEST_FAQ}
                />
              </Paragraph>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion.Root>
      </Box>

      <Text
        css={{
          fontSize: '{fontSizes.fineprint}',
          color: 'textMedium',
          textAlign: 'center',
          lineHeight: '{lineHeights.400}',
        }}
      >
        *Based on active renters with available credit score data as of June 2024. Individual results may vary. Credit
        scores are determined by credit bureaus based upon existing credit profile, credit utilization, payment history
        and other credit factors.
      </Text>
    </Box>
  );
}
